// src/weare/WeAreLinkModal.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal, Button, Message } from "semantic-ui-react";
import { openPopup } from "./popup";
import Configuration from "../config/Configuration";
import { authHeaderSet, authHeader } from "../helpers/auth-header";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onLinked: (payload) => void
 */
export default function WeAreLinkModal({ open, onClose, onLinked }) {
  const config = new Configuration();

  const runIdRef = useRef(0);

  // UI-states
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("ask"); // ask | idle | hti-done | access-done
  const [error, setError] = useState(null);
  const [webId, setWebId] = useState(null);

  // popups & timers
  const pollerRef = useRef(null);
  const popupRef = useRef(null);
  const unmountedRef = useRef(false);
  const persistPopupRef = useRef(false);

  const normalizeWebId = (wid) =>
    typeof wid === "string" && wid.trim().length ? wid.trim() : null;

  const clearPoller = () => {
    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
      pollerRef.current = null;
    }
  };

  const safeSet = (fn) => !unmountedRef.current && fn();

  const closePopupIfOpen = () => {
    try {
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    } catch { }
    popupRef.current = null;
  };

  const waitForPopupNavigation = (w, { timeoutMs = 10000, intervalMs = 120 } = {}) =>
    new Promise((resolve, reject) => {
      if (!w) return reject(new Error("Popup kon niet geopend worden."));
      const start = Date.now();
      const tick = () => {
        if (w.closed) return reject(new Error("Popup werd gesloten voordat de pagina opende."));
        try {
          const href = w.location?.href;
          if (!href || href === "about:blank") {
            if (Date.now() - start > timeoutMs)
              return reject(new Error("Timeout bij openen van access-grant tabblad."));
            return setTimeout(tick, intervalMs);
          }
          return resolve();
        } catch {
          return resolve();
        }
      };
      setTimeout(tick, intervalMs);
    });

  const fetchHtiSession = useCallback(async () => {
    const r = await fetch(config.WELLDATA_GET_HTI_CALL, {
      method: "GET",
      headers: { ...authHeader() },
      cache: "no-store",
    });
    if (!r.ok) throw new Error("HTI sessiestatus ophalen mislukt");
    return r.json();
  }, [config.WELLDATA_GET_HTI_CALL]);


  const handleHtiSessionUpdate = useCallback((data) => {
    const { expiresAt } = data || {};
    const wid = normalizeWebId(data?.webId);
    const expired = expiresAt && new Date(expiresAt).getTime() < Date.now();

    if (expired) {
      closePopupIfOpen();
      safeSet(() => {
        setBusy(false);
        setError("HTI-sessie is verlopen. Probeer opnieuw.");
        setPhase("idle");
      });
      return true;
    }
    if (wid) {
      closePopupIfOpen();
      safeSet(() => {
        setWebId(wid);
        setPhase("hti-done");
        setBusy(false);
      });
      return true;
    }
    return false;
  }, []);

  const checkActiveAccessGrant = useCallback(async () => {
    const url = config.WELLDATA_ACCESS_GRANT_CALL;
    console.log("[WeAreLinkModal] GET access-grant:", url);

    try {
      const r = await fetch(url, {
        method: "GET",
        headers: { ...authHeader() },
        cache: "no-store",
      });

      console.log("[WeAreLinkModal] access-grant status:", r.status);

      if ([401, 404, 204].includes(r.status)) return false;
      if (!r.ok) return false;

      const data = await r.json().catch(() => ({}));
      console.log("[WeAreLinkModal] access-grant data:", data);

      // tolerant: support {active:true} of {_embedded:{...}} of {status:"ACTIVE"} etc.
      const active =
        data?.active === true ||
        data?.isActive === true ||
        data?.grant?.active === true ||
        data?.accessGrant?.active === true ||
        data?.status === "ACTIVE" ||
        data?.state === "ACTIVE";

      return !!active;
    } catch (e) {
      console.warn("[WeAreLinkModal] access-grant error:", e);
      return false;
    }
  }, [config.WELLDATA_ACCESS_GRANT_CALL]);


  // check bestaande grant wanneer modal opent
  useEffect(() => {
    if (!open) return;
    console.log("[WeAreLinkModal] opened -> running checks");

    const runId = ++runIdRef.current;   // uniek id per run
    let cancelled = false;

    (async () => {
      setBusy(true);
      setError(null);

      try {
        const hasGrant = await checkActiveAccessGrant();
        if (cancelled || runId !== runIdRef.current) return;

        if (hasGrant) {
          // ✅ meteen sluiten, en ook state resetten zodat UI niet terugflipt
          setPhase("ask");
          setWebId(null);
          if (typeof onClose === "function") onClose();
          return;
        }

        const hti = await fetchHtiSession();
        if (cancelled || runId !== runIdRef.current) return;

        const wid = normalizeWebId(hti?.webId);
        setWebId(wid);
        setPhase(wid ? "hti-done" : "ask");
      } catch (e) {
        if (cancelled || runId !== runIdRef.current) return;
        setError(e.message || "Er ging iets fout.");
        setPhase("ask");
      } finally {
        if (!cancelled && runId === runIdRef.current) setBusy(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, checkActiveAccessGrant, fetchHtiSession, onClose]);


  // ---------------- actions ----------------
  const startWeAreLogin = useCallback(() => {
    // Optie A: redirect naar keycloak (jij zei: eventueel redirect)
    // Zet hier jouw echte route / login start.
    window.location.assign("/login");
  }, []);

  const startAccessRequest = useCallback(async () => {
    setError(null);
    setBusy(true);

    try {
      const hasGrant = await checkActiveAccessGrant();
      if (hasGrant) {
        if (typeof onClose === "function") onClose();
        return;
      }

      if (!webId) {
        setError("Je moet eerst inloggen via WeAre om een kluis te koppelen.");
        setPhase("ask");
        return;
      }
      setError(null);
      setBusy(true);

      const r = await fetch(config.WELLDATA_ACCESS_REQUEST_CALL, {
        method: "GET",
        headers: { ...authHeader() },
      });
      if (!r.ok) throw new Error("Access request init failed");
      const data = await r.json();
      const launchUrl = data["grantUrl"] || data.url;
      if (!launchUrl) throw new Error("Geen Access Grant URL in response");
      const w = openPopup(launchUrl);
      if (!w) throw new Error("Popup is geblokkeerd door de browser");
      popupRef.current = w;
      await waitForPopupNavigation(w);
      persistPopupRef.current = true;
      if (typeof onClose === "function") onClose();
    } catch (e) {
      safeSet(() => setError(e.message));
    } finally {
      safeSet(() => setBusy(false));
    }
  }, [webId, config.WELLDATA_ACCESS_REQUEST_CALL, onClose]);

  // ---------------- postMessage ----------------
  useEffect(() => {
    unmountedRef.current = false;
    async function handleAccessGrant(grant) {
      try {
        setBusy(true);
        const resp = await fetch("/solidAsset/store-access-grant", {
          method: "POST",
          headers: { ...authHeaderSet(), "Content-Type": "application/json" },
          body: JSON.stringify({ grant }),
        });
        if (!resp.ok) throw new Error("Store access grant failed");
        const data = await resp.json();
        persistPopupRef.current = false;
        closePopupIfOpen();
        setPhase("access-done");
        setBusy(false);
        if (typeof onLinked === "function") onLinked(data);
        if (typeof onClose === "function") onClose();
      } catch (err) {
        safeSet(() => { setError(err.message); setBusy(false); });
      }
    }
    function onMessage(e) {
      if (e.origin !== window.location.origin) return;
      if (!e.data?.type) return;
      if (e.data.type === "WEARE_HTI_DONE") {
        fetchHtiSession().then(handleHtiSessionUpdate).finally(() => closePopupIfOpen());
      }
      if (e.data.type === "WEARE_ACCESS_GRANTED") {
        persistPopupRef.current = false;
        closePopupIfOpen();
        handleAccessGrant(e.data.grant);
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      unmountedRef.current = true;
      window.removeEventListener("message", onMessage);
      clearPoller();
      if (!persistPopupRef.current) closePopupIfOpen();
    };
  }, [fetchHtiSession, handleHtiSessionUpdate, onLinked, onClose]);

  // ---------------- UI ----------------
  return (
    <Modal open={open} onClose={onClose} size="tiny" closeIcon>
      <Modal.Header>WeAre koppelen</Modal.Header>
      <Modal.Content>
        {error && <Message negative content={error} />}
        {phase === "ask" && (
          <p>Om gegevens in een WeAre-kluis op te slaan moet je eerst inloggen via WeAre.</p>
        )}
        {phase === "hti-done" && (
          <Message positive content="Identiteit bevestigd. Je kan nu toegang verlenen tot je We Are Kluis." />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button disabled={busy} onClick={onClose}>Nee, overslaan</Button>

        {phase === "ask" && (
          <Button color="blue" loading={busy} onClick={startWeAreLogin}>
            Inloggen via WeAre
          </Button>
        )}

        {phase === "hti-done" && (
          <Button color="blue" loading={busy} onClick={startAccessRequest}>
            Verleen Toegang
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}